import { mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Colour by the key — a legend at the top, a board of things below.
 *
 * The key says which colour each thing gets: this shape is red, that one is blue. The child works
 * through the board applying it. It is the first activity here where the rule is GIVEN rather than
 * found, and that is the whole point of it — every other game in the bundle asks a child to work
 * out what is going on, and this one asks them to hold an arbitrary instruction and carry it out
 * accurately several times in a row.
 *
 * **The nearest relative is `ef-rule-switch`, and they are opposites.** There the rule is hidden and
 * has to be inferred from feedback; here it is printed at the top of the screen and the difficulty
 * is entirely in the applying. A child can be good at one and hopeless at the other, which is why
 * they are separate families rather than levels of one.
 *
 * **Two kinds of key, and the second one restarts easy.** Levels 1–5 key on SHAPE, which needs no
 * counting; 6–8 key on HOW MANY, which does. The count levels begin with a two-entry key even
 * though the shape levels ended with a four-entry one, and that is deliberate rather than an
 * oversight — it is the same argument sudoku makes when it swaps shapes for digits. A child meeting
 * a new kind of key is not where they were with the old one, and pretending otherwise would record
 * a counting problem as a collapse in a skill they still have.
 */
/** What the key reads. */
export const KEY_BY_SHAPE = 0;
export const KEY_BY_COUNT = 1;
/** Shapes available to a shape key. The renderer draws them; nothing here unpacks one. */
export const CODE_KEY_SHAPES = 6;
/** The largest group a count key asks a child to read. */
export const CODE_KEY_MAX_COUNT = 4;
/** Colours a key may use. The renderer holds the hues. */
export const CODE_KEY_COLORS = 4;
export const CODE_KEY_MIN_DIFFICULTY = 1;
export const CODE_KEY_MAX_DIFFICULTY = 8;
/**
 * Eight levels: a longer key, then a fuller board, then a key that has to be counted.
 *
 * Six things is the ceiling on the board. Three columns on a 360 px phone give tiles around 106 px,
 * and a fourth column would put them at 79 — on the touch floor, on a surface where every tile is
 * tapped several times.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { kind: KEY_BY_SHAPE, keySize: 2, items: 4 };
        case 2:
            return { kind: KEY_BY_SHAPE, keySize: 2, items: 6 };
        case 3:
            return { kind: KEY_BY_SHAPE, keySize: 3, items: 4 };
        case 4:
            return { kind: KEY_BY_SHAPE, keySize: 3, items: 6 };
        case 5:
            return { kind: KEY_BY_SHAPE, keySize: 4, items: 6 };
        // The key starts being counted. Two entries again: see the note at the top of this file.
        case 6:
            return { kind: KEY_BY_COUNT, keySize: 2, items: 4 };
        case 7:
            return { kind: KEY_BY_COUNT, keySize: 3, items: 6 };
        default:
            return { kind: KEY_BY_COUNT, keySize: 4, items: 6 };
    }
}
export function generateCodeKey(task, formId, trialIndex, difficulty) {
    if (difficulty < CODE_KEY_MIN_DIFFICULTY || difficulty > CODE_KEY_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { kind, keySize, items } = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * The key's entries, drawn fresh every trial.
     *
     * A fixed legend — circle always red — would be learnt in two sittings and the game would stop
     * being about reading a key at all. Shuffling which shape means which colour is what keeps the
     * legend the only route to the answer.
     */
    const looks = kind === KEY_BY_COUNT
        ? // Counts are consecutive from one. A key of "one dot" and "four dots" would be easier than
            // one of "two dots" and "three dots", so the run is fixed and only the colours move.
            Array.from({ length: keySize }, (_, i) => i + 1)
        : shuffle(Array.from({ length: CODE_KEY_SHAPES }, (_, i) => i), rand).slice(0, keySize);
    const key = kind === KEY_BY_COUNT ? [...looks] : looks;
    // Which colour each look gets is drawn for both kinds: for counts it is the ONLY thing that
    // varies, and without it every trial at a level would be the same board.
    const order = shuffle(Array.from({ length: keySize }, (_, i) => i), rand);
    const legend = order.map((i) => key[i]);
    /**
     * Every entry appears on the board at least once.
     *
     * A key with an unused entry is a key the child never has to read, and at a two-entry key it
     * would leave a board that is entirely one colour — which is a trial that teaches "tap once
     * each" rather than "read the legend".
     */
    const board = shuffle([
        ...legend.map((_, colour) => colour),
        ...Array.from({ length: items - keySize }, () => Math.floor(rand() * keySize)),
    ], rand);
    return {
        kind,
        key: legend,
        items: board.map((colour) => legend[colour]),
        answer: board,
    };
}
/**
 * The fewest taps a correct board can take.
 *
 * A tile cycles: the first tap gives the first colour, the next the second, and after the last it
 * comes back round. So reaching colour `c` costs `c + 1` taps and a perfect run costs the sum. The
 * renderer records the difference between this and what the child actually spent, which is the
 * process signal a bare right/wrong throws away — two children can both finish a board while one
 * went straight to each colour and the other cycled past it three times.
 */
export function minimumTaps(stimulus) {
    return stimulus.answer.reduce((total, colour) => total + colour + 1, 0);
}
/** Every tile the colour its key says. Nothing partial: the board is the answer. */
export function scoreCodeKeyTrial(stimulus, painted) {
    return (painted.length === stimulus.answer.length &&
        stimulus.answer.every((colour, i) => painted[i] === colour));
}
//# sourceMappingURL=code-key.js.map